import cloneDeep from 'lodash/cloneDeep'

import type {
  Shortlist,
  CompactShortlist,
  ShortlistSection,
  ShortlistTrackItem,
} from 'types/shortlist'
import type { Track, TrackId } from 'types/track'

import { getNestedTracksCount } from '@disco/utils/tracks'

type GenericShortlist = Shortlist | CompactShortlist

/**
 * Removes binary image data from a shortlist to reduce payload size.
 *
 * Creates a deep copy of the shortlist and removes binary image fields
 * that are not needed for certain operations. This helps reduce memory
 * usage and transfer sizes when working with shortlists.
 *
 * @param shortlist - The shortlist to clean (Shortlist or CompactShortlist)
 * @returns A new shortlist object without binary image data
 *
 * @example
 * ```typescript
 * const cleanShortlist = removeBinaryImagesInShortlist(originalShortlist)
 * // cleanShortlist will not have album_image, album_image_preview, etc.
 * ```
 */
export function removeBinaryImagesInShortlist<T extends GenericShortlist>(
  shortlist: T,
): T {
  const updatedShortlist = cloneDeep(shortlist)

  delete updatedShortlist.album_image
  delete updatedShortlist.album_image_preview

  if (updatedShortlist?.showcase?.type) {
    delete updatedShortlist.showcase.artist_override_image
    delete updatedShortlist.showcase.artist_override_image_preview
  }

  return updatedShortlist
}

/**
 * Calculates the index of an item excluding section items.
 *
 * When working with shortlists that contain both track items and section headers,
 * this function calculates what the index would be if only track items were counted.
 *
 * @param index - The original index in the mixed array
 * @param items - Array containing both track items and section items
 * @returns The index counting only track items (excluding sections)
 *
 * @example
 * ```typescript
 * const items = [trackItem1, sectionItem, trackItem2, trackItem3]
 * getIndexWithoutSections(3, items) // returns 2 (3rd item is trackItem3, but it's the 2nd track)
 * ```
 */
export function getIndexWithoutSections(
  index: number,
  items: (ShortlistTrackItem | ShortlistSection)[],
): number
export function getIndexWithoutSections(
  index: number | null | undefined,
  items: (ShortlistTrackItem | ShortlistSection)[],
): number | undefined
export function getIndexWithoutSections(
  index: number | null | undefined,
  items: (ShortlistTrackItem | ShortlistSection)[],
): number | undefined {
  if (index == null) {
    return undefined
  }
  let result = index
  for (let i = 0; i < index; i++) {
    const item = items[i]
    if (item && !('track' in item)) {
      result--
    }
  }
  return result
}

/**
 * Merges track items with section headers at their correct positions.
 *
 * Takes separate arrays of track items and section headers, and creates
 * a single array with sections inserted at their specified positions.
 *
 * @param items - Array of track items
 * @param sections - Array of section headers with position information
 * @returns Combined array with sections inserted at correct positions
 *
 * @example
 * ```typescript
 * const tracks = [track1, track2, track3]
 * const sections = [{ position: 1, title: 'Section 1' }]
 * const combined = getItemsWithSections(tracks, sections)
 * // Result: [track1, sectionHeader, track2, track3]
 * ```
 */
export const getItemsWithSections = (
  items: ShortlistTrackItem[],
  sections: ShortlistSection[],
): (ShortlistTrackItem | ShortlistSection)[] => {
  const result = [...items]

  const sortedSections = [...sections].sort((a, b) => a.position - b.position)

  for (let i = 0; i < sortedSections.length; i++) {
    const section = sortedSections[i]

    // @ts-expect-error todo fix later
    result.splice(section.position + i, 0, section)
  }

  return result
}

/**
 * Determines the presentation type of a shortlist based on its showcase settings.
 *
 * Analyzes the shortlist's showcase configuration to return a human-readable
 * presentation type string.
 *
 * @param shortlist - The shortlist object to analyze
 * @returns String describing the presentation type
 *
 * @example
 * ```typescript
 * const albumShortlist = { showcase: { type: 'album' } }
 * getShortlistPresentationType(albumShortlist) // returns 'Album showcase'
 *
 * const artistShortlist = { showcase: { type: 'artist' } }
 * getShortlistPresentationType(artistShortlist) // returns 'Artist showcase'
 *
 * const defaultShortlist = { showcase: null }
 * getShortlistPresentationType(defaultShortlist) // returns 'Default playlist'
 * ```
 */
export const getShortlistPresentationType = ({
  showcase,
}: Shortlist): string => {
  if (showcase?.type === 'album') {
    return 'Album showcase'
  }
  if (showcase?.type === 'artist') {
    return 'Artist showcase'
  }
  return 'Default playlist'
}

/**
 * Checks if a shortlist contains any tracks with nested/main track content.
 *
 * Determines whether the shortlist has tracks that contain additional
 * nested tracks or main track content.
 *
 * @param shortlist - The shortlist to check (optional)
 * @returns True if the shortlist contains tracks with nested content
 *
 * @example
 * ```typescript
 * const shortlistWithMainTracks = { items: [{ track: { nested_tracks: [...] } }] }
 * hasShortlistContainMainTrack(shortlistWithMainTracks) // returns true
 *
 * const simpleShortlist = { items: [{ track: { nested_tracks: [] } }] }
 * hasShortlistContainMainTrack(simpleShortlist) // returns false
 * ```
 */
export const hasShortlistContainMainTrack = (
  shortlist?: Shortlist | null,
): boolean => {
  if (!shortlist) return false

  return shortlist?.items?.some(({ track }) => {
    return getNestedTracksCount(track) > 0
  })
}

/**
 * Determines if the formats tab should be disabled for a shortlist.
 *
 * Checks if all tracks in the shortlist have only one file format,
 * in which case the formats tab would be unnecessary.
 *
 * @param shortlist - The shortlist to check
 * @param tracks - Map of track IDs to track objects
 * @returns True if the formats tab should be disabled
 *
 * @example
 * ```typescript
 * const shortlist = { items: [{ track: { id: '1' } }] }
 * const tracks = { '1': { all_files: { 'mp3': {...} } } }
 * isFormatsTabDisabled(shortlist, tracks) // returns true (only one format)
 *
 * const tracksMultiFormat = { '1': { all_files: { 'mp3': {...}, 'wav': {...} } } }
 * isFormatsTabDisabled(shortlist, tracksMultiFormat) // returns false (multiple formats)
 * ```
 */
export const isFormatsTabDisabled = (
  shortlist: Shortlist | null | undefined,
  tracks: Partial<Record<TrackId, Track>>,
): boolean => {
  if (!shortlist) return false

  return shortlist.items.every(item => {
    const track = tracks[item.track.id]
    return Object.keys(track?.all_files || {}).length <= 1
  })
}

export type PlaylistCreatorLocation =
  | 'Explorer'
  | 'Search'
  | 'Discover'
  | undefined
export type PlaylistCreatorActionType =
  | 'Load Page'
  | 'Click Button'
  | 'Drag Track'
  | 'Duplicate Playlist'
  | 'Edit Playlist'
  | 'Add Track to Playlist'
  | 'Generate Showcase'

export type PlaylistCreatorVisibility = 'Visible' | 'Hidden'

export type ToggleStateSettings = {
  actionType: PlaylistCreatorActionType
  visible: PlaylistCreatorVisibility
}

export type ToggleUserSettings = {
  location: PlaylistCreatorLocation
  settings: {
    [key: string]: ToggleStateSettings
  }
}

export type FormattedPlaylistToggleSettings = {
  prev: ToggleUserSettings | undefined
  curr: ToggleUserSettings
}

const formatPlaylistVisibilitySettings = (
  userSettings: ToggleUserSettings | undefined,
): ToggleUserSettings | null => {
  if (!userSettings?.location) return null
  const locationKey = userSettings.location.toLowerCase()
  const settings = userSettings.settings[locationKey]

  if (!settings) return null
  return {
    location: userSettings.location,
    settings: { [locationKey]: settings },
  }
}

export const setPlaylistCreatorUserSettings = (
  prevSettings: ToggleUserSettings,
  currSettings: ToggleUserSettings,
): FormattedPlaylistToggleSettings | undefined => {
  const currentLocation = currSettings?.location
  if (!currSettings || !currentLocation) return

  const formattedCurrentSettings =
    formatPlaylistVisibilitySettings(currSettings)
  const formattedPrevSettings = formatPlaylistVisibilitySettings(prevSettings)

  if (!formattedCurrentSettings) return undefined

  return {
    prev: formattedPrevSettings || undefined,
    curr: formattedCurrentSettings,
  }
}

type PlaylistCreatorMixpanelProps = {
  Context: string
  ['Action type']: string
  ['New state']: string
}

export const getPlaylistCreatorSettingsMixpanelProperties = ({
  curr,
}: FormattedPlaylistToggleSettings): PlaylistCreatorMixpanelProps | null => {
  if (!curr.location) return null
  const currSettings = curr.settings[curr.location.toLowerCase()]
  if (!currSettings) return null

  return {
    Context: curr.location,
    ['Action type']: currSettings.actionType,
    ['New state']: currSettings.visible,
  }
}
